import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { startCase } from 'lodash';
import { forkJoin } from 'rxjs';
import { defaultTableDefOptions } from '../../../helpers';
import { BranchService } from '../../../service/branch.service';
import { LocationService } from '../../../service/location.service';
import { SubscriptionService } from '../../../service/subscription.service';
import { LocationChangelogsService } from '../../../service/location-changelogs.service';
import { LocationChangelog, LocationHistoryChangelogItem } from '../../../types/location-changelog';
import { Branch, LocationResponse } from '../../../types';

type PageLocationHistory = LocationChangelog & { changelogParsed: LocationHistoryChangelogItem[] };

@Component({
  selector: 'app-installer-dashboard',
  templateUrl: './location-change-history.component.html',
  styleUrls: ['./location-change-history.component.scss'],
  standalone: false,
})
export class LocationChangeHistoryComponent implements OnInit {
  private subscriptionService = inject(SubscriptionService);
  private locationChangelogsService = inject(LocationChangelogsService);
  private locationService = inject(LocationService);
  private branchService = inject(BranchService);

  public locationId = input<number>();
  public branchId = input<number>();
  public branchData = input<Branch>();
  public locationData = input<LocationResponse>();

  public isLoading = signal<boolean>(false);
  public history = signal<PageLocationHistory[]>([]);

  private customerType = signal<string>('');
  private branchName = signal<string>('');
  private locationName = signal<string>('');

  public subtitle = computed(() => (this.branchName() && this.locationName() ? `${this.branchName()} - ${this.locationName()}` : ''));

  public readonly tableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public readonly columnDefs: ColDef<PageLocationHistory>[] = [
    {
      field: 'created_at',
      headerName: 'Happened At',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      maxWidth: 200,
      sort: 'desc',
      sortIndex: 0,
      cellRenderer: (params) => {
        return params.value ? new Date(params.value).toLocaleString() : '';
      },
    },
    {
      field: 'email',
      headerName: 'Changed By',
      cellDataType: 'string',
      filter: 'agTextColumnFilter',
      maxWidth: 400,
      valueGetter: (params) => {
        return params.data.email || params.data.actor;
      },
    },
    {
      headerName: 'Change Log',
      flex: 1,
      autoHeight: true,
      cellDataType: 'string',
      cellRenderer: (params) => {
        const changelog = params.data.changelogParsed
          .map((item) => `<div class="whitespace-break-spaces leading-5">${item.html}</div>`)
          .join('');
        return changelog;
      },
    },
  ];

  ngOnInit() {
    this.isLoading.set(true);

    forkJoin({
      branch: this.branchService.getBranchById(this.branchId()),
      location: this.locationService.getLocation({ locationId: this.locationId() }),
      locationHistory: this.locationChangelogsService.getLocationChangelogs(this.branchId(), this.locationId()),
    }).subscribe(async ({ branch, location, locationHistory }) => {
      this.locationName.set(location.name);
      this.branchName.set(branch.name);
      this.customerType.set(branch.customer_type);

      const changes: PageLocationHistory[] = await Promise.all(
        locationHistory.items.map(async (history) => {
          const baseChangelog = JSON.parse(history.change_log);
          const finalChangelog = await Promise.all(
            Object.keys(baseChangelog).map(async (k) => {
              const changelog = {
                key: this.preprocessKey(k),
                value: await this.preprocessValue(k, baseChangelog[k]),
                html: '',
              };

              if (changelog.value) {
                changelog.html = `<strong>${startCase(changelog.key)}</strong> was updated to ${changelog.value}`;
              } else {
                changelog.html = `<strong>${startCase(changelog.key)}</strong> value was removed`;
              }

              return changelog;
            }),
          );

          return {
            ...history,
            created_at: new Date(history.created_at),
            changelogParsed: finalChangelog,
          };
        }),
      );

      this.history.set(changes);
      this.isLoading.set(false);
    });
  }

  preprocessKey(key: string) {
    if (key === 'stripe_price_id') {
      return 'Subscription Plan';
    }

    return key.replace(/_/g, ' ');
  }

  preprocessValue(key: string, value: string): Promise<string> {
    if (key === 'stripe_price_id' && this.customerType()) {
      return this.subscriptionService.getTheftPlanName(value, this.customerType()).toPromise();
    }
    return Promise.resolve(value);
  }
}
